import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import HallBookingHero from "./HallBooking";
import "./MamangamHallBooking.css";
import "./HallBookingDetail.css";

import { api } from "../config/api";

const PAGE_KEY = "mamangam-hall-booking";

const WhatsAppIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4.83574 12.3296L5.31889 12.6113C6.12606 13.082 7.0434 13.3333 8.00066 13.3333C10.9462 13.3333 13.334 10.9455 13.334 7.99998C13.334 5.05446 10.9462 2.66665 8.00066 2.66665C5.05513 2.66665 2.66732 5.05446 2.66732 7.99998C2.66732 8.95751 2.9188 9.87518 3.38977 10.6825L3.67136 11.1652L3.2357 12.7662L4.83574 12.3296ZM1.33677 14.6666L2.23808 11.3543C1.66329 10.369 1.33398 9.22298 1.33398 7.99998C1.33398 4.31808 4.31875 1.33331 8.00066 1.33331C11.6825 1.33331 14.6673 4.31808 14.6673 7.99998C14.6673 11.6818 11.6825 14.6666 8.00066 14.6666C6.77806 14.6666 5.63234 14.3376 4.64724 13.7631L1.33677 14.6666Z"
      fill="white"
    />
  </svg>
);

const buildWhatsAppLink = (number, prefilledText) => {
  if (!number) return "#contact";

  const cleanNumber = String(number)
    .replace(/[^\d+]/g, "")
    .replace(/^\+/, "");
  const encodedText = encodeURIComponent(prefilledText || "");

  return `https://wa.me/${cleanNumber}${encodedText ? `?text=${encodedText}` : ""}`;
};

const resolveInitialTabIndex = (location, sections) => {
  const tabId =
    location.state?.initialTab ||
    new URLSearchParams(location.search).get("tab") ||
    sections[0]?.slug;

  const index = sections.findIndex((tab) => tab.slug === tabId);
  return index >= 0 ? index : 0;
};

const HallBookingDetail = () => {
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const data = await api.getPageConfigByKey(PAGE_KEY);

      const transformedData = {
        hero: {
          title: data.title || "Mamangam Hall Booking",
          subtitle: data.subtitle || "Mamangam Hall",
          description:
            data.description ||
            "Mamangam Hall at MY Cinemas is a premium multi-purpose space.",
        },
        sections: (data.sections || [])
          .filter((section) => section.is_active)
          .sort((a, b) => a.display_order - b.display_order)
          .map((section) => ({
            id: section.id,
            slug: section.slug,
            title: section.title,
            detailImageUrl: section.data?.detail_image_url || "",
            iconSvg: section.data?.icon_svg || "",
            points: section.data?.feature_points || [],
            whatsappNumber: section.data?.whatsapp_number || "",
            whatsappPrefilledText: section.data?.whatsapp_prefilled_text || "",
          })),
      };

      setPageData(transformedData);
      // Set initial tab based on URL/state
      const initialIndex = resolveInitialTabIndex(
        location,
        transformedData.sections,
      );
      setActiveTab(initialIndex);
    } catch (error) {
      console.error("Error loading Hall Booking Detail:", error);
      setPageData({
        hero: {
          title: "Mamangam Hall Booking",
          subtitle: "Mamangam Hall",
          description: "",
        },
        sections: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pageData && pageData.sections.length > 0) {
      setActiveTab(resolveInitialTabIndex(location, pageData.sections));
      window.scrollTo(0, 0);
    }
  }, [location.state?.initialTab, location.search, pageData]);

  if (loading) {
    return (
      <div className="hall-page hall-section-loading">
        <div className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  const { hero, sections } = pageData;

  if (sections.length === 0) {
    return (
      <div className="hall-page">
        <HallBookingHero hero={hero} showDetails />
        <div className="container py-5 text-center">
          <p>No categories available at the moment.</p>
        </div>
      </div>
    );
  }

  const currentTab = sections[activeTab] || sections[0];
  const whatsappLink = buildWhatsAppLink(
    currentTab.whatsappNumber,
    currentTab.whatsappPrefilledText,
  );

  return (
    <div className="hall-page">
      <HallBookingHero hero={hero} showDetails />

      <section className="hall-booking-details">
        <div className="container">
          <div className="row g-4">
            {/* Vertical Pills Sidebar */}
            <div className="col-lg-4">
              <div className="vert-pills">
                {sections.map((tab, index) => (
                  <button
                    key={tab.id}
                    className={index === activeTab ? "active" : ""}
                    onClick={() => setActiveTab(index)}
                  >
                    <span
                      className="iconholder"
                      dangerouslySetInnerHTML={{ __html: tab.iconSvg }}
                    />
                    <span>{tab.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Detail Content Panel */}
            <div className="col-lg-8">
              <div className="hall-details">
                <figure>
                  <img src={currentTab.detailImageUrl} alt={currentTab.title} />
                </figure>
                <h4>{currentTab.title}</h4>

                <ul className="hall-feature-list">
                  {currentTab.points.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
                <a
                  href={whatsappLink}
                  className="connect-btn mt-3"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon />
                  Contact
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HallBookingDetail;
